// @flow
import get from 'lodash/get';
import has from 'lodash/has';
import identity from 'lodash/identity';
import { denormalize, normalize, schema } from 'normalizr';

const {
  Array: ArraySchema,
  Entity: BaseEntitySchema,
  Object: ObjectSchema,
  Union: UnionSchema,
  Values: ValuesSchema,
} = schema;

export { ArraySchema, ObjectSchema, UnionSchema, ValuesSchema };

/**
 * Determines whether a definition appears to be a normalization schema, as defined by having
 * normalize and denormalize functions
 *
 * @param  {*} definition - potential schema
 * @return {boolean} whether or not the argument is a schema
 */
export function isSchema(definition: any): boolean {
  return (
    typeof definition === 'object' &&
    typeof definition.normalize === 'function' &&
    typeof definition.denormalize === 'function'
  );
}

/**
 * Creates a function for mapping paths in a result object to flat keys that can be used in a
 * response schema
 *
 * @param  {Object} keyToResultPath - maps keys in the remapped result to paths in the source result
 * @return {Function} a processResult function
 */
export const createPathRemapProcessor = (keyToResultPath: Object): Function =>
  function processResult(result: Object): Object {
    const remappedResult = {};
    // $FlowFixMe
    Object.entries(keyToResultPath).forEach(([key, resultPath]: [string, Array<string>]) => {
      if (has(result, resultPath)) {
        remappedResult[key] = get(result, resultPath);
      }
    });
    return remappedResult;
  };

export class EntitySchema extends BaseEntitySchema {
  constructor(key: string, definition: any = {}, options: Object = {}) {
    super(key, definition, options);

    const { unprocessStrategy = identity } = options;

    this._unprocessStrategy = unprocessStrategy;
  }

  denormalize(...args: any) {
    const result = super.denormalize(...args);
    return typeof this._unprocessStrategy === 'function' ? this._unprocessStrategy(result) : result;
  }
}

export class LocallyNormalizedSchema {
  schema: any;

  constructor(definition: any = {}) {
    this.define(definition);
  }

  define(definition: any = {}) {
    if (isSchema(definition)) {
      this.schema = definition;
    } else if (Array.isArray(definition)) {
      this.schema = new ArraySchema(definition[0]);
    } else {
      this.schema = new ObjectSchema(definition);
    }
  }

  normalize(input: any) {
    return normalize(input, this.schema);
  }

  denormalize({ result, entities }: { result: any, entities: Object }) {
    return denormalize(result, this.schema, entities);
  }
}

type ProcessedResultSchemaOptions = {
  processResult?: Function | Object,
};

type ResponseSchemaOptions = ProcessedResultSchemaOptions & {
  dataKey?: string,
  processEnvelope?: Function | Object,
};

/**
 * Processed Input Schema
 * ======================
 *
 * Use this schema when you want 100% custom processing of some data.
 */
export class ProcessedInputSchema {
  _processInput: Function;
  _unprocessInput: Function;

  constructor(processInput?: Function, unprocessInput?: Function) {
    this._processInput = typeof processInput === 'function' ? processInput : identity;
    this._unprocessInput = typeof unprocessInput === 'function' ? unprocessInput : identity;
  }

  normalize(...args: Array<any>) {
    return this._processInput(...args);
  }

  denormalize(...args: Array<any>) {
    return this._unprocessInput(...args);
  }
}

/**
 * Processed Result Schema
 * =======================
 *
 * This is a variation of the Object/Array schemas that supports a custom processing function after
 * regular normalization takes place. This is intended for cases where there may be meaningful
 * information in the non-entity part of the normalized data, but you don't want to require
 * consumers of the normalized data to know shape of the input.
 */
export class ProcessedResultSchema {
  schema: ArraySchema | ObjectSchema;
  _processResult: Function;

  /**
   * Creates a response schema
   *
   * @param  {Object|Array} definition - schema definition
   * @param  {Object} [options] - response schema options
   * @param  {Function|Object} [options.processResult] - post-normalization processing function,
   *         or an object to use as a key-to-path remapper
   */
  constructor(definition?: Object | Array<any> = {}, options?: ProcessedResultSchemaOptions = {}) {
    this.define(definition, options);
  }

  define(definition?: Object | Array<any> = {}, options?: ProcessedResultSchemaOptions = {}) {
    if (isSchema(definition)) {
      this.schema = definition;
    } else if (Array.isArray(definition)) {
      this.schema = new ArraySchema(definition[0]);
    } else {
      this.schema = new ObjectSchema(definition);
    }

    const { processResult } = options;

    if (typeof processResult === 'function') {
      this._processResult = processResult;
    } else if (typeof processResult === 'object') {
      this._processResult = createPathRemapProcessor(processResult);
    }
  }

  normalize(...args: Array<any>): any {
    const result = this.schema.normalize(...args);
    return typeof this._processResult === 'function' ? this._processResult(result) : result;
  }

  denormalize(...args: Array<any>): any {
    const result = this.schema.normalize(...args);
    return typeof this._processResult === 'function' ? this._processResult(result) : result;
  }
}

/**
 * Response Schema
 * ===============
 *
 * This schema simplifies support for processing of Tinder API responses that use data/meta envelope.
 *
 * In most cases, the important data from the API is contained in the `data` key, and everything
 * else is ignored. Rather than prepending `data` to everything, we treat it as the default.
 *
 * When handling of `meta` or other sibling keys is necessary, custom envelope processing behavior
 * can be specified.
 */
export class ResponseSchema extends ProcessedResultSchema {
  /**
   * Creates a response schema
   *
   * @param  {Object|Array} definition - schema definition
   * @param  {Object} [options] - response schema options
   * @param  {string} [options.dataKey] - key to use for data
   * @param  {Function|Object} [options.processResult] - post-normalization processing function for
   *         the data contained in the response
   * @param  {Function|Object} [options.processEnvelope] - processResult function for the envelope
   */
  constructor(definition?: Object | Array<any> = {}, options?: ResponseSchemaOptions = {}) {
    const { dataKey = 'data', processEnvelope, ...dataOptions } = options;

    if (dataKey) {
      const dataDefinition = { [dataKey]: new ProcessedResultSchema(definition, dataOptions) };
      const processResult = processEnvelope || ((result) => result[dataKey]);
      super(dataDefinition, { processResult });
    } else {
      super(definition, dataOptions);
    }
  }
}
